import { useCallback, useEffect, useState } from "react";

import "../assets/scss/components/_cookie-banner.scss";

type CookieType = "functional" | "marketing";

type CookiePreferences = Record<CookieType, boolean>;

interface CookieBannerProps {
	privacyPolicyUrl: string;
	marketingCookieNames?: string[];
	loadScriptsAfterConsent?: () => void;
}

declare global {
	interface Window {
		dataLayer?: Array<Record<string, unknown>>;
		gtag?: (...args: unknown[]) => void;
	}
}

const COOKIE_TYPES: CookieType[] = ["functional", "marketing"];

const getCookie = (name: string): string | null => {
	if (typeof document === "undefined") return null;

	const cookie = document.cookie
		.split("; ")
		.find((item) => item.startsWith(`${encodeURIComponent(name)}=`));

	return cookie ? decodeURIComponent(cookie.split("=")[1]) : null;
};

const setCookie = (name: string, value: string | number, days: number): void => {
	if (typeof document === "undefined") return;

	const maxAge = 60 * 60 * 24 * days;

	document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
		String(value),
	)}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

const getPreference = (key: string): string | null => {
	if (typeof window === "undefined") return null;

	try {
		return window.localStorage.getItem(key);
	} catch {
		return getCookie(key);
	}
};

const setPreference = (key: string, value: string | number): void => {
	if (typeof window === "undefined") return;

	try {
		window.localStorage.setItem(key, String(value));
	} catch {
		setCookie(key, value, 365);
	}
};

const deleteCookie = (name: string): void => {
	if (typeof window === "undefined" || typeof document === "undefined") return;

	const hostname = window.location.hostname;
	const rootHostname = hostname.replace(/^www\./, "");

	const domains = [
		hostname,
		`.${hostname}`,
		rootHostname,
		`.${rootHostname}`,
	];

	document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;

	domains.forEach((domain) => {
		document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`;
	});
};

const updateGoogleConsent = (preferences: CookiePreferences): void => {
	if (typeof window === "undefined") return;

	const functionalConsent = preferences.functional ? "granted" : "denied";
	const marketingConsent = preferences.marketing ? "granted" : "denied";

	window.gtag?.("consent", "update", {
		analytics_storage: functionalConsent,
		ad_storage: marketingConsent,
		ad_user_data: marketingConsent,
		ad_personalization: marketingConsent,
		personalization_storage: functionalConsent,
		functionality_storage: functionalConsent,
	});

	window.dataLayer = window.dataLayer ?? [];

	window.dataLayer.push({
		event: preferences.functional ? "consent_functional_granted" : "consent_functional_denied",
	});

	window.dataLayer.push({
		event: preferences.marketing ? "consent_marketing_granted" : "consent_marketing_denied",
	});
};

export const CookieBanner = ({
	privacyPolicyUrl,
	marketingCookieNames = [],
	loadScriptsAfterConsent,
}: CookieBannerProps) => {
	const [canShow, setCanShow] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const [isManaging, setIsManaging] = useState(false);

	const [preferences, setPreferences] = useState<CookiePreferences>({
		functional: false,
		marketing: false,
	});

	const reopenCookieSettings = useCallback((): void => {
		const savedPreferences: CookiePreferences = {
			functional: getPreference("functional_cookies") === "1",
			marketing: getPreference("marketing_cookies") === "1",
		};

		setPreferences(savedPreferences);
		setIsManaging(true);
		setIsVisible(true);
		setCanShow(true);
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const savedPreferences: CookiePreferences = {
			functional: getPreference("functional_cookies") === "1",
			marketing: getPreference("marketing_cookies") === "1",
		};

		const bannerHidden = getPreference("banner_hidden") === "1";

		setPreferences(savedPreferences);
		setIsVisible(!bannerHidden);
		setCanShow(true);
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") return;

		window.addEventListener(
			"open-cookie-settings",
			reopenCookieSettings,
		);

		return () => {
			window.removeEventListener(
				"open-cookie-settings",
				reopenCookieSettings,
			);
		};
	}, [reopenCookieSettings]);

	const saveCookieSettings = (values: CookiePreferences): void => {
		setPreference("banner_hidden", 1);

		COOKIE_TYPES.forEach((type) => {
			const value = values[type] ? 1 : 0;

			setPreference(`${type}_cookies`, value);
			setCookie(`${type}_cookies`, value, 365);
		});
	};

	const handlePreferenceChange = (type: CookieType): void => {
		setPreferences((current) => ({
			...current,
			[type]: !current[type],
		}));
	};

	const openCookieSettings = (): void => {
		setIsManaging(true);
	};

	const acceptAllCookies = (): void => {
		const acceptedPreferences: CookiePreferences = {
			functional: true,
			marketing: true,
		};

		setPreferences(acceptedPreferences);
		saveCookieSettings(acceptedPreferences);
		updateGoogleConsent(acceptedPreferences);

		loadScriptsAfterConsent?.();

		setIsManaging(false);
		setIsVisible(false);
	};

	const rejectAllCookies = (): void => {
		const rejectedPreferences: CookiePreferences = {
			functional: false,
			marketing: false,
		};

		setPreferences(rejectedPreferences);
		saveCookieSettings(rejectedPreferences);
		updateGoogleConsent(rejectedPreferences);

		marketingCookieNames.forEach(deleteCookie);

		setIsManaging(false);
		setIsVisible(false);
	};

	const saveCookieSelection = (): void => {
		saveCookieSettings(preferences);
		updateGoogleConsent(preferences);

		if (preferences.marketing) {
			loadScriptsAfterConsent?.();
		} else {
			marketingCookieNames.forEach(deleteCookie);
		}

		setIsManaging(false);
		setIsVisible(false);
	};

	if (!canShow || !isVisible) {
		return null;
	}

	return (
		<div id="cookies_banner" className="cookies-banner">
			<div className="cookie-container">
				<div className="cookie-explanation">
					<h3>Cookies &amp; Privacy</h3>
					<p>We use cookies and similar technologies to understand how you use our website and improve your experience. With your permission, we may also share some information with trusted partners, such as Google, to show you more relevant content and advertisements. You can accept or reject these cookies now, and change your preferences at any time through the website&apos;s cookie settings.</p>

					<div className={`manage-cookies ${isManaging ? "active" : ""}`}>
						<div className="essential-cookies cookie-category">
							<div className="cookie-header-action">
								<h4>Essential</h4>
								<input type="checkbox" id="essential-cookies-check" data-cookie-type="essential" className="cookie-check" checked disabled readOnly/>
							</div>
							<p>These cookies are essential for the website to function properly and cannot be disabled.</p>
						</div>

						<div className="functional-cookies cookie-category">
							<div className="cookie-header-action">
								<h4>Functional</h4>
								<input type="checkbox" id="functional-cookies-check" data-cookie-type="functional" className="cookie-check" checked={preferences.functional} onChange={() => handlePreferenceChange("functional")}/>
							</div>
							<p>These cookies allow the website to remember your preferences and provide enhanced features.</p>
						</div>

						<div className="marketing-cookies cookie-category">
							<div className="cookie-header-action">
								<h4>Marketing / Analytics</h4>
								<input type="checkbox" id="marketing-cookies-check" data-cookie-type="marketing" className="cookie-check" checked={preferences.marketing} onChange={() => handlePreferenceChange("marketing")}/>
							</div>
							<p>These cookies are used to understand browsing behaviour and show personalised advertisements.</p>
						</div>
					</div>

					<div className="cookie-settings">
						{!isManaging ? (
							<button type="button" id="open-cookies" className="cookie-settings-link" onClick={openCookieSettings} aria-label="Manage cookie preferences">
								Manage cookies
							</button>
						) : (
							<button type="button" id="save-cookies" className="cookie-settings-link" onClick={saveCookieSelection} aria-label="Save cookie preferences">
								Save cookies
							</button>
						)}
						<a href="/privacy-policy" aria-label="Privacy policy">
							<u>View privacy policy</u>
						</a>
					</div>
				</div>

				<div className="cookie-actions">
					<button type="button" className="btn" id="accept-cookies" onClick={acceptAllCookies} aria-label="Accept all cookies">
						Accept all
					</button>
				</div>
			</div>
		</div>
	);
};